import { ResponseStatuses } from '@/enums';
import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, Response,
} from '@/types';
import { DispatchStatuses } from '@/enums/statuses/dispatches';
import { PaymentsStatuses } from '@/enums/statuses/payments/';
import { Orders } from '@/modules/orders/types';
import { Dispatches, DownloadFilters } from '../types';

interface DispatchesList extends BaseResponse, ApiPagination {
  data: {
    items: Dispatches.Dispatch[];
    contracts: Record<'name', string>[];
    organizations: Orders.OrderOrganization;
    organizations_external: Record<'name', string>[];
  };
}

interface DispatchDetail extends BaseResponse {
  data: Dispatches.DispatchDetailedInfo;
}

interface DispatchCorrective extends BaseResponse {
  data: Dispatches.DispatchСorrective
}

interface DispatchReturn extends BaseResponse {
  data: {
    items: Dispatches.DispatchReturn[];
  };
}

interface DispatchDocuments extends BaseResponse {
  data: {
    documents: { name: string; path: string }[];
  };
}

interface DispatchesDownloadList {
  data: {
    categories: Dispatches.Categories[],
    products: Dispatches.Products[],
  };
}

export class DispatchesService extends BaseApiService {
  private readonly base_path = '/order/register-realization';

  async get_dispatches(filters: FiltersPagination<Filters>): Promise<DispatchesList> {
    const payload = filters;

    const { data } = await this.http_client.get<DispatchesList>(this.base_path, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_dispatches_download(filters: DownloadFilters): Promise<DispatchesDownloadList> {
    const payload = filters;
    const url = `${this.base_path}/report`;

    const { data } = await this.http_client.get<DispatchesDownloadList>(url, {
      params: {
        filters_exist: payload.filters_exist,
        filters: payload.filters,
      },
    });

    return data;
  }

  async get_dispatch_by_id(id: string | number): Promise<DispatchDetail> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<DispatchDetail>(url);

    return data;
  }

  async get_dispatch_return(id: string | number): Promise<DispatchReturn> {
    const url = `${this.base_path}/returns?register_realization_id=${id}`;
    const { data } = await this.http_client.get<DispatchReturn>(url);

    return data;
  }

  async send_dispatch_return(id: string | number, form_data: FormData): Promise<DispatchDocuments> {
    const url = `${this.base_path}/${id}/products?_method=patch`;
    const { data } = await this.http_client.post<DispatchDocuments>(url, form_data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return data;
  }

  async get_dispatch_corrective_by_id(id: string | number): Promise<DispatchCorrective> {
    const url = `${this.base_path}/correctives/${id}`;
    const { data } = await this.http_client.get<DispatchCorrective>(url);

    return data;
  }

  async get_dispatch_as_documents(id: string | number): Promise<Response<Dispatches.ExelInfo>> {
    const url = `${this.base_path}/${id}/report`;
    const { data } = await this.http_client.get<Response<Dispatches.ExelInfo>>(url);

    return data;
  }
}

export const default_dispatch_details = (): Dispatches.DispatchDetailedInfo => ({
  id: 0,
  create_at: '',
  total_volume: '',
  total_weight: '',
  amount_contract: '',
  organization: null,
  products: [],
  status: null,
  contract: '',
  files: [],
  track_number: '',
  order_id: 0,
  status_edo: null,
});

export const default_dispatch_corrective = (): Dispatches.DispatchСorrective => ({
  id: 0,
  amount_contract: null,
  contract: '',
  create_at: '',
  external_number: '',
  number_upd: '',
  organization: '',
  organization_external: '',
  parent_realization_id: 0,
  parent_realization_number: '',
  products: [],
  status: null,
  status_edo: null,
});
